import { useState, useEffect, useRef, useCallback } from "react";
import { GetVisualPanelList } from "../../api/visualpanel-functions";
import globalVariables from "../../global-variables";
import Carousel from "react-bootstrap/Carousel";
import { Container } from "reactstrap";
import Image from 'react-image-webp';
import { useNavigate } from "react-router-dom";
import $ from 'jquery';
import '../../assets/css/ds-visual-swiper.css';

// fallback interval for a video slide before its real duration is known (large = don't auto-advance yet)
const VIDEO_INTERVAL = 1000 * 60 * 60;

// --- Load the YouTube IFrame API once, resolve when ready ---
let ytApiPromise = null;

function loadYouTubeApi() {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise((resolve) => {
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof prev === "function") prev();
            resolve(window.YT);
        };
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(tag);
    });
    return ytApiPromise;
}

// Accepts a raw 11-char id OR any common YouTube URL, returns the 11-char id
function getYouTubeId(value) {
    if (!value) return null;
    const str = String(value).trim();

    // already just an id
    if (/^[\w-]{11}$/.test(str)) return str;

    // try to parse the v= query param first (most reliable for watch links)
    try {
        const url = new URL(str);
        const v = url.searchParams.get("v");
        if (v && /^[\w-]{11}$/.test(v)) return v;
    } catch (e) { /* not a full URL, fall through */ }

    // youtu.be/ID, /embed/ID, /shorts/ID, /live/ID, /v/ID
    const m = str.match(/(?:youtu\.be\/|\/embed\/|\/shorts\/|\/live\/|\/v\/)([\w-]{11})/);
    return m ? m[1] : null;
}

// --- One video slide, manages its own player ---
function VideoSlide({ videoId, isActive, onEnded, onDuration, loop = false, start = 0, end = 0 }) {
    const hostRef = useRef(null);
    const playerRef = useRef(null);
    const [ready, setReady] = useState(false);

    // keep latest values without recreating the player
    const onEndedRef = useRef(onEnded);
    const onDurationRef = useRef(onDuration);
    const loopRef = useRef(loop);
    onEndedRef.current = onEnded;
    onDurationRef.current = onDuration;
    loopRef.current = loop;

    // reports the video's real duration (in seconds) once the player knows it
    const reportDuration = (target) => {
        const duration = target.getDuration();
        if (duration && onDurationRef.current) onDurationRef.current(duration);
    };

    // create player once per videoId
    useEffect(() => {
        let cancelled = false;
        loadYouTubeApi().then((YT) => {
            if (cancelled || !hostRef.current) return;
            const el = document.createElement("div"); // YT replaces this node, not one React tracks
            hostRef.current.appendChild(el);
            playerRef.current = new YT.Player(el, {
                videoId,
                playerVars: {
                    autoplay: 0, controls: 0, mute: 1, rel: 0, fs: 0,
                    modestbranding: 1, playsinline: 1, disablekb: 1, iv_load_policy: 3,
                    start: start || 0, ...(end ? { end } : {}),
                },
                events: {
                    onReady: (e) => {
                        setReady(true);
                        reportDuration(e.target);
                    },
                    onStateChange: (e) => {
                        if (e.data === YT.PlayerState.PLAYING) {
                            reportDuration(e.target);
                        }
                        if (e.data === YT.PlayerState.ENDED) {
                            if (loopRef.current) {
                                e.target.seekTo(start || 0);
                                e.target.playVideo();
                            } else if (onEndedRef.current) {
                                onEndedRef.current();
                            }
                        }
                    },
                },
            });
        });
        return () => {
            cancelled = true;
            const p = playerRef.current;
            if (p && p.destroy) { try { p.destroy(); } catch (err) {} }
            playerRef.current = null;
        };
    }, [videoId]);

    // play when this slide is active, pause + rewind otherwise
    useEffect(() => {
        const p = playerRef.current;
        if (!p || !ready) return;
        try {
            if (isActive) {
                p.mute();
                p.seekTo(start || 0);
                p.playVideo();
            } else {
                p.pauseVideo();
                p.seekTo(start || 0);
            }
        } catch (err) {}
    }, [isActive, ready, start]);

    return <div className="youtube-container" ref={hostRef} />;
}

export default function VisualpanelBlock() {

    const navigate = useNavigate();
    const [index, setIndex] = useState(0);
    const [visualpanels, setVisualpanelList] = useState([]);
    // real duration (ms) of each video slide, keyed by slide index, once the YT player reports it
    const [videoDurations, setVideoDurations] = useState({});

    const OnGetVisualpanelSuccess = (p_response) => {
        
        setVisualpanelList(p_response);
    };

    const handleSelect = (selectedIndex) => setIndex(selectedIndex);

    // advance to next slide when a video finishes
    const goNext = useCallback(() => {
        setIndex((prev) => {
            const total = visualpanels.length;
            return total ? (prev + 1) % total : 0;
        });
    }, [visualpanels.length]);

    // record a video slide's real duration so the carousel waits exactly that long before advancing
    const handleVideoDuration = useCallback((slideIndex, seconds) => {
        const ms = Math.round(seconds * 1000);
        setVideoDurations((prev) => (prev[slideIndex] === ms ? prev : { ...prev, [slideIndex]: ms }));
    }, []);

    useEffect(() => {
        let _objVP = new Object();
        _objVP._visualpanel_id = null;
        _objVP._fieldname = null;
        _objVP._isactive = 1;
        GetVisualPanelList(_objVP, OnGetVisualpanelSuccess, navigate);
    }, []);

    useEffect(() => {
        if (visualpanels.length === 1) $(".carousel-indicators").remove();
    }, [visualpanels]);

    // shared caption so image + video slides look identical
    const renderCaption = (val) => (
        <Carousel.Caption className="slider-caption">
            <Container>
                <div className="caption-container ms-auto text-end mb-5">
                    <div className="display-6 fw-light mb-2" style={{ color: val.visualpanel_title_color }}>
                        {val.visualpanel_title}
                    </div>
                    {val.visualpanel_caption != null && val.visualpanel_caption != "" ? (
                        <small className="h4 d-block fw-bold d-md-block d-none" style={{ color: val.visualpanel_caption_color }}>
                            {val.visualpanel_caption}
                        </small>
                    ) : null}
                </div>
            </Container>
        </Carousel.Caption>
    );

    return (
        <div className="container-fluid bg-light px-0 h-100">
            <div className="d-md-none d-none">
                <Image className="img-fluid" loading="lazy"
                    alt="Research and Development Programs for Green Energy, Environment and Sustainability"
                    title="Research and Development Programs for Green Energy, Environment and Sustainability"
                    width={650} height={500}
                    src={(globalVariables.variables.strDomainUrl + "images/visualpanel/mob-visual.jpg")}
                    webp={(globalVariables.variables.strDomainUrl + "images/visualpanel/mob-visual.webp")}
                />
            </div>
            <div className="visual-container-1920 d-md-block d-block">
                <Carousel fade
                    activeIndex={index}
                    onSelect={handleSelect}
                    interval={3000}
                    nextIcon={false}
                    prevIcon={false}
                >
                    {visualpanels.length > 0 ? visualpanels.map((val, i) => {
                        const videoId = getYouTubeId(val.visualpanel_video);
                        const hasImage = val.visualpanel_pic != null && val.visualpanel_pic != "";
                        const showVideo = !!videoId;
                        const hasLink = val.visualpanel_link != null && val.visualpanel_link != "";

                        const media = showVideo ? (
                            <div className="visual-video-item">
                                <VideoSlide
                                    videoId={videoId}
                                    isActive={index === i}
                                    loop={visualpanels.length === 1}
                                    onEnded={goNext}
                                    onDuration={(seconds) => handleVideoDuration(i, seconds)}
                                    start={0}
                                />
                                <div className="bg-mask position-absolute top-0 start-0 end-0 bottom-0" />
                            </div>
                        ) : (
                            <img loading="lazy" className="img-fluid"
                                src={globalVariables.variables.strApiUrl + "/visualpanel/" + val.visualpanel_pic}
                                alt={val.visualpanel_title} title={val.visualpanel_title}
                                width={1920} height={750} />
                        );

                        const content = <>{media}{renderCaption(val)}</>;
                        
                        return (
                            <Carousel.Item
                                key={val.visualpanel_id ?? i}
                                interval={showVideo ? (videoDurations[i] || VIDEO_INTERVAL) : 3000}
                            >
                                {hasLink && !showVideo ? (
                                    <a href={val.visualpanel_link}
                                        target={val.visualpanel_isextlink == "1" ? "_blank" : "_self"}>
                                        {content}
                                    </a>
                                ) : content}
                            </Carousel.Item>
                        );
                    }) : null}
                </Carousel>
            </div>
        </div>
    );
}